import React, { useState } from "react";
import i18n from "i18next";
import { Grid, Typography } from "@mui/material";
import { makeValidate } from "mui-rff";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { FORM_ERROR } from "final-form";
import {
  PageBox,
  PageHeader,
  FormAlert,
  FormTextField,
  FormButton,
} from "./Crediting.style";
import FormDateField from "../../components/Mui/DateFormField";
import FormListField from "../../components/Mui/ListFormField";
import { format } from "date-fns";

import { CreditingFinancialSchema } from "../../schema/CreditingFinancialSchema";
import { CreditingRegularSchema } from "../../schema/CreditingRegularSchema";
import { TabTitle } from "../../utils/GeneralFunctions";
import { useCrediting } from "../../providers/CreditingProvider";
import { CreditingDataInterface } from "../../interfaces/CreditingDataInterface";
import { useAuth } from "../../providers/Auth";
import { SelectChangeEvent } from '@mui/material/Select';

import { useDialog } from "react-dialog-async";
import CreditDialog from "../../components/CreditDialog/CreditDialog";

const Crediting = () => {
  const auth = useAuth();
  const creditor = useCrediting();
  const myDialog = useDialog(CreditDialog);

  if (auth){
    auth.refreshSession();
  }

  const { t } = useTranslation();
  const [severity, setSeverity] = useState("success");
  const [submitLoading, setSubmitLoading] = React.useState(false);

  const creditingType = auth?.user?.creditingType;
  const mpcs : any = auth?.user?.mpcs;
  const defaultMpcsCode = mpcs[0].code;

  const [activityDatePickerValue, setActivityDatePickerValue] = React.useState<Date>(new Date());
  const [marketingCodeValue, setMarketingCodeValue] = React.useState<string>(defaultMpcsCode);
  const [minPoints, setMinPoints] = React.useState(mpcs[0].minPoints);
  const [maxPoints, setMaxPoints] = React.useState(mpcs[0].maxPoints);
  const [creditPointsHint, setCreditPointsHint] = React.useState<string>("["+ defaultMpcsCode + "] Min: "+ mpcs[0].minPoints + " Max: "+ mpcs[0].maxPoints);

  const validate = makeValidate((creditingType === "FINANCIAL")?CreditingFinancialSchema:CreditingRegularSchema, (error) => {
    return i18n.t(error.message);
  });

  function createReviewData(name: string, detail: string) {
    return { name, detail };
  }

  const handleDateChange = async (value: Date) => {
    setActivityDatePickerValue(value);
  };

  const handleSelectChange = async (event: SelectChangeEvent) => {
    const code = event.target.value as string;
    setMarketingCodeValue(code);
    if (mpcs){
      const marketCodeData : any = mpcs?.find(el => el.code === code);
      setMinPoints(marketCodeData.minPoints);
      setMaxPoints(marketCodeData.maxPoints);
      setCreditPointsHint("["+ code + "] Min: "+ marketCodeData.minPoints + " Max: "+ marketCodeData.maxPoints);
    }
  };


  // PACTOOL - Search and Validate
  TabTitle(t("app.name") + " - " + t("crediting.page.title"));

  let formData = {
    aeroplanNumber: "",
    lastName: "",
    activityDate: "",
    storeNumber: "",
    referenceNumber: "",
    marketingCode: "",
    creditedMiles: "",
  };

  const onSubmit = async (values) => {

    // check  program code
    if (marketingCodeValue === " ") {
      setSeverity("error");
      const errorMessage = t("crediting.block-form.error.marketingCode.required");
      return { [FORM_ERROR]: errorMessage};
    }
    
    // check the points are in range based on program code
    if (values.creditedMiles < minPoints || values.creditedMiles > maxPoints) {
      setSeverity("error");
      const errorMessage = t("crediting.block-form.error.credited.miles.invalid") + " Min: "+ minPoints + " Max: "+ maxPoints;
      return { [FORM_ERROR]: errorMessage};
    }

    // review data for dialog popup
    const reviewData = (creditingType === "FINANCIAL")?[
      createReviewData(t("crediting.block-form.field.aeroplannumber"), values.aeroplanNumber),
      createReviewData(t("crediting.block-form.field.lastname"), values.lastName),
      createReviewData(t("crediting.block-form.field.activityDate"), format(activityDatePickerValue, "dd/MM/yyyy")),
      createReviewData(t("crediting.block-form.field.storeNumber"), values.storeNumber),
      createReviewData(t("crediting.block-form.field.referenceNumber"), values.referenceNumber),
      createReviewData(t("crediting.block-form.field.productTypeCode"), values.productTypeCode),
      createReviewData(t("crediting.block-form.field.partnerReference"), values.partnerReference),
      createReviewData(t("crediting.block-form.field.marketingCode"), marketingCodeValue),
      createReviewData(t("crediting.block-form.field.creditedMiles"), values.creditedMiles),
    ]:[
      createReviewData(t("crediting.block-form.field.aeroplannumber"), values.aeroplanNumber),
      createReviewData(t("crediting.block-form.field.lastname"), values.lastName),
      createReviewData(t("crediting.block-form.field.activityDate"), format(activityDatePickerValue, "dd/MM/yyyy")),
      createReviewData(t("crediting.block-form.field.storeNumber"), values.storeNumber),
      createReviewData(t("crediting.block-form.field.referenceNumber"), values.referenceNumber),
      createReviewData(t("crediting.block-form.field.marketingCode"), marketingCodeValue),
      createReviewData(t("crediting.block-form.field.creditedMiles"), values.creditedMiles),
    ];

    // pop up dialog for user to review and confirm information
    const response = await myDialog.show({
      title: t("crediting.dialog.modal.title"),
      message: t("crediting.dialog.modal.message"),
      rows: reviewData
    });

    // check to see we proceed or cancel submission
    if (!response) {
      setSeverity("error");
      return { [FORM_ERROR]: "crediting.block-form.error.cancelled.submission" };
    }

    // prepare submission
    setSubmitLoading(true);

    const query = {
      memberId: values.aeroplanNumber,
      lastName: values.lastName,
      activityDate: format(activityDatePickerValue, "yyyy-MM-dd"),
      storeNumber: values.storeNumber,
      referenceNumber: values.referenceNumber,
      productTypeCode: values.productTypeCode,
      partnerReference: values.partnerReference,
      marketingCode: marketingCodeValue,
      creditedMiles: values.creditedMiles,
    } as CreditingDataInterface;


    const result: any = await creditor.creditTransaction(query);
    setSubmitLoading(false);

    if (result.status === "FAIL" || result.error) {
      setSeverity("error");
      return { [FORM_ERROR]: result.message };
    }
    
    if (result.status === "SUCCESS") {
      setSeverity("success");
      return { [FORM_ERROR]: "crediting.block-form.error.validation.success" };
    }

    setSeverity("error");
    return { [FORM_ERROR]: "crediting.block-form.error.validation.failed" };
  };

  return (
    <>
      <PageBox>
        <Grid container sx={{ pt: 0 }}>
          <Grid item xs={12} sm={6}>
            <PageHeader>{t("crediting.page.title")}</PageHeader>
            <Typography
              component="p"
              variant="lead"
              sx={{ fontSize: "1.0em", marginBottom: "2rem" }}
            >
              {t("crediting.page.description")}
            </Typography>
            <Form
              onSubmit={onSubmit}
              initialValues={formData}
              validate={validate}
              render={({
                submitError,
                handleSubmit,
                form,
                submitting,
                pristine
              }) => (
                <form onSubmit={handleSubmit}>
                  {submitError && (
                    <FormAlert severity={severity}>{t(submitError)}</FormAlert>
                  )}

                  <Typography component="p" variant="lead">
                    {t("crediting.block-form.title")}
                  </Typography>

                  <FormTextField
                    name="aeroplanNumber"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("crediting.block-form.field.aeroplannumber")}
                    id="aeroplanNumber"
                  />

                  <FormTextField
                    name="lastName"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("crediting.block-form.field.lastname")}
                    id="lastName"
                  />

                  <FormDateField
                    name="activityDate"
                    label={t("crediting.block-form.field.activityDate")}
                    inputFormat="dd/MM/yyyy"
                    value={activityDatePickerValue}
                    handleChange={(value) => handleDateChange(value)}
                    id="activityDate"
                  />

                  <FormTextField
                    name="storeNumber"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("crediting.block-form.field.storeNumber")}
                    id="storeNumber"
                  />

                  <FormTextField
                    name="referenceNumber"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("crediting.block-form.field.referenceNumber")}
                    id="referenceNumber"
                  />

                  {creditingType === "FINANCIAL" && (
                    <>
                      <FormTextField
                        name="productTypeCode"
                        variant="filled"
                        InputProps={{ disableUnderline: true }}
                        label={t("crediting.block-form.field.productTypeCode")}
                        id="productTypeCode"
                      />

                      <FormTextField
                        name="partnerReference"
                        variant="filled"
                        InputProps={{ disableUnderline: true }}
                        label={t("crediting.block-form.field.partnerReference")}
                        id="partnerReference"
                      />
                    </>
                  )}

                  <FormListField
                    name="marketingCode"
                    label={t("crediting.block-form.field.marketingCode")}
                    list={mpcs}
                    value={marketingCodeValue}
                    handleChange={(event) => handleSelectChange(event)}
                    id="marketingCode"
                  />

                  <FormTextField
                    name="creditedMiles"
                    variant="filled"
                    InputProps={{ disableUnderline: true }}
                    label={t("crediting.block-form.field.creditedMiles")}
                    id="creditedMiles"
                  />

                  <Typography
                    variant="caption"
                    display="block"
                    gutterBottom
                    sx={{ paddingLeft: "17px", paddingBottom: "17px" }}
                  >
                    {creditPointsHint}
                  </Typography>

                  <FormButton
                    type="button"
                    onClick={form.reset}
                    disabled={submitting || pristine}
                  >
                    {t("crediting.block-form.button.clear")}
                  </FormButton>

                  <FormButton
                    type="submit"
                    loadingPosition="end"
                    loading={submitLoading}
                    endIcon={<div />}
                    disabled={submitting}
                  >
                    {t("crediting.block-form.button.submit")}
                  </FormButton>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </PageBox>
    </>
  );
};

export default Crediting;


